/* ── 194 §B / §C.1 — THE TWO SEARCH ROWS, ONE COMPONENT ──────────────────────
 *
 * DEVIATION, declared: the brief names no new file, and nothing existing fit.
 * Two surfaces render the SAME two rows — the Communication section (§B: the
 * section IS the configuration) and the deep-search drawer's top (§C.1: the
 * configuration lives on, so a new search never needs the drawer closed) —
 * and the rows carry state (a chosen subset of parties, two periods, a text,
 * one switch), a modal and four date boxes read at the click. Duplicating
 * that in two files would be the drift `emailRowFilter.ts`'s header warns
 * about, and the section cannot import it from the drawer (the drawer is a
 * lazy chunk — a static import would pull it into the main bundle). So it is
 * its own file, and the closing report leads with it.
 *
 * WHAT THE ROWS ARE (194D §B's grammar, one grid per row so the two tails
 * align to the pixel and the switch can sit under the « De » label):
 *   1  QUICK SEARCH ON THE PARTIES' ADDRESSES: · [N parties: a@x ; b@y ; …]
 *      · Period · [menu] · [from] [to] · [Search]
 *   2  FREE-TEXT SEARCH: · [text] · Period · [menu] · [from] [to] · [Search]
 *      and, on its own line under the « De » label, the switch "also search
 *      the whole period" (C.5, off by default; 194E §B — RULED, Laurent: it
 *      aligns with the TEXT « De », which supersedes 194D B.5's "under the
 *      Période menu"). The help line that said what
 *      Microsoft does with words (A.2c: all the words, anywhere — never an
 *      exact phrase) is the text box's hover now (194D B.4), same key.
 *   The titles are the existing keys in capitals (B.1, the Messages pane's
 *   IMPORT); the field in the middle FILLS; the period block is pinned to the
 *   right edge as one thing (B.3). Every control wears `.commField` — the
 *   Saisies de temps filter row's height (194D §A, App.css) — and only the
 *   two Search buttons keep their own height, which the host names.
 *
 * THE PARTIES FIELD (194D B.2) is a field, not a button: the border, colour
 * and edges of the fields beside it, stretching, reading the count and then
 * the chosen addresses, cut at the end with an ellipsis when the room runs
 * out. It opens a small modal listing every party address of the matter
 * (the SERVED list, B.1b: address, name, role), each ticked by default, in
 * the shape the parties section's mail dialog already uses for addresses
 * (App.tsx's `mailDlg`: a label per person, a checkbox, the name, the
 * address). A matter-less host (the dashboard) has no parties: it passes
 * `parties={null}` and the quick row is not rendered.
 *
 * THE DATE BOXES show the period the menu chose (B.1c) and stay editable;
 * typing a date puts the menu on "Free from/to…", exactly as the pre-194
 * surfaces did (174 §3). Both boxes of a row are read AT THE CLICK for
 * `validity.badInput` (192 D.2): a box showing segments it cannot read holds
 * "" — an open side — and the search is refused with the box named, through
 * `onRefuse`, changing nothing else.
 *
 * NO `t` IS OWNED HERE: the host hands its translator down, so the i18n gates
 * read every literal key at these call sites and the host's language rules.
 * The role words are literal keys in a switch, never a concatenated key —
 * `i18n-keys-complete` cannot resolve `t("x." + role)` (its first incident).
 * `import React` is here for `react/react-in-jsx-scope`, the committed
 * config's rule the ratchet counts once per element; a new src/ file is held
 * at zero warnings, and no `window`/`document` is read here.
 */
import React from "react";
import FreeDateInput from "./FreeDateInput"; // 213B §15 — the free window's edges
import { useRef, useState } from "react";
import type { CSSProperties, Dispatch, ReactNode, RefObject } from "react";
import MenuSelect from "./MenuSelect";
import { ModalLayout, ListNotLoaded } from "./SharedUI";
import { PAGE_LISTS, useListsNotLoaded } from "../lib/listLoad"; // 211 §10 — the matter's parties, when they could not load
import { periodBounds, PERIOD_OPTIONS } from "../lib/periodRange";
import type { PeriodKind } from "../lib/periodRange";
import { dateBoxRefusal } from "../lib/emailReach";
import type { Translate } from "../lib/interestRefusal";
import { FS, RAD } from "../lib/tokens";
import { NO_AUTOFILL } from "../lib/noAutofill"; // 213B §8 — no sign-ins, no contacts, in the drawers' text fields

/** One served party address (B.1b): `GET …/emails/parties`. */
export interface EmailSearchParty {
  email: string;
  name: string;
  /** the slot code: CL · CLC · ADV · ADV2 · ADVCO · ADVA · EXP · MED · ARB */
  role: string;
}

/** A row's period: the menu's kind and the two boxes it wrote (or the user typed). */
export interface EmailSearchWindow {
  period: PeriodKind;
  from: string;
  to: string;
}

/** The whole configuration the two rows hold — what a Search hands the drawer. */
export interface EmailSearchConfig {
  /** the chosen subset of the served party list; `null` = every party (the default) */
  addresses: string[] | null;
  quick: EmailSearchWindow;
  q: string;
  deep: EmailSearchWindow;
  /** 194 C.5 — the walk beside the fast search; off by default */
  walk: boolean;
}

export type EmailSearchKind = "quick" | "deep";

/** A window from a period kind: the kind's bounds, or the pair given for "free".
 *  198 §C — `axisInput` is `periodBounds`' seam (D.3): the host's firm settings
 *  object, through which the two accounting-year kinds read the firm's start;
 *  nothing (the old `null`) resolves them to 1 January. */
export function emailSearchWindow(period: PeriodKind, free?: { from?: string; to?: string }, axisInput: unknown = null): EmailSearchWindow {
  if (period === "free") return { period, from: free?.from ?? "", to: free?.to ?? "" };
  const b = periodBounds({ kind: period }, axisInput);
  return { period, from: b?.[0] ?? "", to: b?.[1] ?? "" };
}

/** The configuration a section opens on: every party, the last ten days on
 *  both rows (194 B.1d), no text, the completion off. */
export function defaultEmailSearchConfig(opts?: { q?: string; period?: PeriodKind; from?: string; to?: string }, axisInput: unknown = null): EmailSearchConfig {
  const period = opts?.period ?? "last10days";
  const free = opts && (opts.from !== undefined || opts.to !== undefined) ? { from: opts.from, to: opts.to } : undefined;
  const w = emailSearchWindow(period, free, axisInput);
  return { addresses: null, quick: { ...w }, q: opts?.q ?? "", deep: { ...w }, walk: false };
}

/** The addresses a quick search sends: the chosen subset, or every party. */
export function chosenPartyAddresses(cfg: EmailSearchConfig, parties: readonly EmailSearchParty[] | null | undefined): string[] {
  const all = (parties ?? []).map((p) => p.email);
  if (cfg.addresses === null) return all;
  const chosen = new Set(cfg.addresses);
  return all.filter((e) => chosen.has(e));
}

/** The role's word — literal keys, one per slot code, so the census can read them. */
export function partyRoleWord(role: string, t: Translate): string {
  switch (role) {
    case "CL": return t("emailParties.role.CL", { fallback: "Client" });
    case "CLC": return t("emailParties.role.CLC", { fallback: "Client contact" });
    case "ADV": return t("emailParties.role.ADV", { fallback: "Opposing party" });
    case "ADV2": return t("emailParties.role.ADV2", { fallback: "Additional opposing party" });
    case "ADVCO": return t("emailParties.role.ADVCO", { fallback: "Opposing party contact" });
    case "ADVA": return t("emailParties.role.ADVA", { fallback: "Opposing counsel" });
    case "EXP": return t("emailParties.role.EXP", { fallback: "Expert" });
    case "MED": return t("emailParties.role.MED", { fallback: "Mediator" });
    case "ARB": return t("emailParties.role.ARB", { fallback: "Arbiter" });
    default: return role;
  }
}

/* 194D E.2 — THE PERIOD TAIL, EXTRACTED (moved, not bettered) out of the rows'
   `windowControls` closure so the messages drawer's period row wears the same
   controls: Period · [menu] · From · [from] · To · [to], then the caller's own
   button. Rendered as a FRAGMENT of grid items into the caller's row grid, so
   the e-mail rows and the messages drawer lay the tail on the same tracks
   (150 · 130 · 130). The menu's options are the caller's — the e-mail rows
   offer the ten periods, the messages drawer puts "All dates" at their head
   (194 B.1d: "Toutes les dates" is not a period and only the surfaces that
   had it keep it). */
const FIELD_LABEL_STYLE: CSSProperties = { fontSize: FS.s10, color: "var(--tm)", whiteSpace: "nowrap" };

interface PeriodWindowFieldsProps {
  t: Translate;
  value: EmailSearchWindow;
  /** the next window, whole: the menu's bounds, or the typed date under "free" */
  onChange: Dispatch<EmailSearchWindow>;
  /** [kind, label] in the caller's order */
  options: readonly (readonly [string, string])[];
  /** 198 §C — `periodBounds`' axis input (the host's firm settings object): a
   *  picked accounting-year kind resolves against the firm's start (D.3) */
  axisInput?: unknown;
  /** the menu's stacking level: a drawer passes its own; the section passes nothing */
  menuZIndex?: number;
  /** the two boxes, read AT THE CLICK by the caller for `validity.badInput` (192 D.2) */
  fromRef: RefObject<HTMLInputElement>;
  toRef: RefObject<HTMLInputElement>;
  /** the row's button, after the tail */
  children?: ReactNode;
  /** 197 C.7 — a date box COMMITTED: left, or Enter. The messages drawer
   *  re-imports on it (its own committed-window idiom, 194D §E: its window IS
   *  a search); the e-mail rows pass nothing on every host — 197B §A.1(4):
   *  on the search rows the window is configuration and Rechercher is the
   *  act, nothing re-searches by itself. */
  onCommit?: () => void;
}

export function PeriodWindowFields({ t, value, onChange, options, axisInput = null, menuZIndex, fromRef, toRef, children, onCommit }: Readonly<PeriodWindowFieldsProps>) {
  const periodLabel = t("te.filter.period", { fallback: "Period" });
  const onDateKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") onCommit?.();
  };
  return (
    <>
      <label style={FIELD_LABEL_STYLE}>{periodLabel}</label>
      <MenuSelect
        ariaLabel={periodLabel}
        title={periodLabel}
        value={value.period}
        onChange={(v) => {
          onChange(emailSearchWindow(v as PeriodKind, { from: value.from, to: value.to }, axisInput));
        }}
        options={options}
        className="field-input commField"
        {...(menuZIndex != null ? { zIndex: menuZIndex } : {})}
      />
      <label style={FIELD_LABEL_STYLE}>{t("emailSearch.from", { fallback: "From" })}</label>
      <FreeDateInput
        ref={fromRef}
        className="field-input commField"
        value={value.from}
        onCommit={(v) => {
          onChange({ period: "free", from: v, to: value.to });
        }}
        onBlur={onCommit}
        onKeyDown={onDateKey}
      />
      <label style={FIELD_LABEL_STYLE}>{t("emailSearch.to", { fallback: "To" })}</label>
      <FreeDateInput
        ref={toRef}
        className="field-input commField"
        value={value.to}
        onCommit={(v) => {
          onChange({ period: "free", from: value.from, to: v });
        }}
        onBlur={onCommit}
        onKeyDown={onDateKey}
      />
      {children}
    </>
  );
}

/* 194D B.1 — the row titles in CAPITALS, set the way the Messages pane's
   IMPORT is (WhatsappImportPane's pane label): text-transform on the
   existing keys, not new keys. 197 §A — at module scope and EXPORTED: the
   e-mails drawer's first row names the search that made it in this same
   dress, in amber (one definition, never a second spelling). */
export const EMAIL_ROW_TITLE_STYLE: CSSProperties = {
  fontSize: FS.s10,
  fontWeight: 700,
  color: "var(--ts)",
  textTransform: "uppercase",
  letterSpacing: ".4px",
  whiteSpace: "nowrap",
};
/* 197 §A — the criteria of the row that made what is on screen: the field's
   edge and its text in the same amber as its title. */
const ACTIVE_FIELD_STYLE: CSSProperties = { borderColor: "var(--am)", color: "var(--am)" };

interface EmailWalkSwitchProps {
  t: Translate;
  checked: boolean;
  onChange: Dispatch<boolean>;
  className?: string;
  style?: CSSProperties;
}
/** 194 C.5 — the completion switch « chercher aussi dans toute la période »,
 *  hookless at module scope (197's extraction, kept): rendered by the rows
 *  alone, under « De » on row 2 (194E §B), on every host — 197B §A.1(2)
 *  brought it back onto the drawer's rows with the tail, so the drawer has
 *  no copy of its own and no other file reads this. A change sets the
 *  configuration; the next Rechercher reads it. */
function EmailWalkSwitch({ t, checked, onChange, className, style }: Readonly<EmailWalkSwitchProps>) {
  return (
    <label
      className={className}
      style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: FS.s10_5, color: "var(--ts)", whiteSpace: "nowrap", cursor: "pointer", ...style }}
      title={t("emailSearch.rows.walk_also_title", { fallback: "Beside the fast search (Microsoft's index, at most 1,000 messages of the period), walk the whole period message by message and merge the two — slower, complete; each row says how it was found." })}
    >
      <input
        type="checkbox"
        checked={checked}
        style={{ margin: 0 }}
        onChange={(e) => {
          onChange(e.target.checked);
        }}
      />
      {t("emailSearch.rows.walk_also", { fallback: "also search the whole period" })}
    </label>
  );
}

/* The three callbacks are typed by React's own `Dispatch<T>`: a function type
   written here with a named parameter is a warning the per-file eslint ratchet
   counts (base no-unused-vars) — emailArchiveClient.ts's rule. */
interface Props {
  t: Translate;
  value: EmailSearchConfig;
  /** the next configuration, whole */
  onChange: Dispatch<EmailSearchConfig>;
  /** the served party list; `null` on a matter-less host — the quick row is not rendered */
  parties: readonly EmailSearchParty[] | null;
  /** a Search pressed with both of its boxes readable: which row */
  onSearch: Dispatch<EmailSearchKind>;
  /** a box the browser cannot read: the sentence naming it (192 D.2) */
  onRefuse: Dispatch<string>;
  /** a search is running: the two buttons wait */
  busy?: boolean;
  /** no mailbox connection: the two buttons are disabled */
  disabled?: boolean;
  /** 194 C.4 — the period menus' stacking level: a drawer passes its own
   *  (`DRAWER_Z + 2`); the section passes nothing and takes the idiom's 201 */
  menuZIndex?: number;
  /** the parties modal's level: above a drawer (`DRAWER_Z + 4`), the default 300 elsewhere */
  modalZIndex?: number;
  /** the two Search buttons' height — the one thing 194D §A leaves to the
   *  host: the section's 30px, the drawer's 28px. Every field takes the
   *  Saisies de temps row's height through `.commField`. */
  buttonHeight?: number;
  /** 197 §A [RULED — Laurent, 2026-09-19] — the row whose search made what is
   *  on screen wears AMBER, title and criteria, so it and the drawer's first
   *  row (which names that search in the same amber) read as one thing. The
   *  drawer passes the kind it last ran; the section passes the kind that
   *  opened the drawer while it is open, and nothing once it is closed. */
  activeKind?: EmailSearchKind | null;
  /** 198 §C — the firm settings object, `periodBounds`' axis input for the
   *  rows' windows (D.3): the two accounting-year kinds read the firm's start
   *  off it; absent → 1 January */
  firmSettings?: unknown;
}

/** 211 §10 — the one page list the quick row picks from. */
const PARTY_LISTS = [PAGE_LISTS.emailParties];

export default function EmailSearchRows({
  t,
  value,
  onChange,
  parties,
  onSearch,
  onRefuse,
  busy = false,
  disabled = false,
  menuZIndex,
  modalZIndex,
  buttonHeight = 30,
  activeKind = null,
  firmSettings = null,
}: Readonly<Props>) {
  const [partiesOpen, setPartiesOpen] = useState(false);
  // 211 §10 — the host's party list could not load (its host layers it: CaseEmailsSection)
  const partiesNotLoaded = useListsNotLoaded(PARTY_LISTS).length > 0;
  const quickFromRef = useRef<HTMLInputElement>(null);
  const quickToRef = useRef<HTMLInputElement>(null);
  const deepFromRef = useRef<HTMLInputElement>(null);
  const deepToRef = useRef<HTMLInputElement>(null);

  const periodOptions = PERIOD_OPTIONS.map((o) => [o.kind, t(o.key, { ...o.vars, fallback: o.fallback })] as [string, string]); // 213B §4 — the quarters' `n`
  /* 197 §A — the title, and the criteria beside it, in amber for the row whose
     search made what is on screen (`--am`, the token 194D §G already uses for
     the other-party question — no new colour); the other row keeps its dress. */
  const titleOf = (kind: EmailSearchKind): CSSProperties =>
    activeKind === kind ? { ...EMAIL_ROW_TITLE_STYLE, color: "var(--am)" } : EMAIL_ROW_TITLE_STYLE;
  const fieldOf = (kind: EmailSearchKind): CSSProperties => (activeKind === kind ? ACTIVE_FIELD_STYLE : {});
  /* 194D B.3 — ONE GRID PER ROW: title · the filling field · Period · [menu]
     · From · [from] · To · [to] · [Search]. The tail's tracks are fixed, so
     the two rows' period blocks align to the pixel and read as one block
     pinned to the right edge; the middle track is the one that gives. The
     switch under row 2 is a second grid line starting at the « De » label's
     track — 194E §B [RULED — Laurent, 2026-09-18, superseding 194D B.5]: the
     checkbox aligns with the TEXT « De », not with the menu before it. */
  /* 195 §E.3 (194D and 194E surfaced: the rows did not wrap under the case
     grid's 899px media query, and at 1280 the quick row's Rechercher ended
     97.6 CSS px past the card's edge — ONE defect, the tail FIXED where it
     should YIELD): the row is a wrapping flex line now and the tail — 194D
     B.3's seven fixed tracks (Period · [menu] · From · [from] · To · [to] ·
     [Search]) — ONE grid item of it, `margin-left: auto`. Where the title,
     the field's 140px floor and the tail no longer fit on one line the WHOLE
     tail drops to a second line at the right edge, whole, aligned with the
     other row's; nothing overflows and nothing inside the tail moves. The
     switch under row 2 lives INSIDE the tail, on its second grid line at the
     « De » label's track (194E §B stands). MEASURED at 1280: the tail (586
     CSS px) drops whole to the second line. AND AT 1024 AND 899 THE CARD IS
     NARROWER THAN THE TAIL ITSELF (the case grid's column: 582 and 480 CSS
     px), so the tail's TRACKS are a stylesheet rule under a container query
     on the row (App.css `.commTail`): seven tracks where the row can hold
     them, one control per line where it cannot — label · field, the field
     filling the line, Rechercher and the switch spanning it. Same seven
     children, no second component. */
  /* 197B §B [MEASURED, then cured — Laurent at the prod glance: "CC has
     centered the title and a part of the fields, now it looks wobbly: put
     everything again on the superior row, checkbox is lonely, alone, sole on
     its row just under, but will survive"]. MEASURED at 1600 on the running
     staging: the pane is 1350 CSS px, the row 1322, the tail 587 — the row
     FITS on one line, the container query is silent (seven tracks), and 196
     §B's `.detail-card` box moves the row by 2 px (its padding) and wraps
     nothing; the emulated pre-196 box measures the same. What put the title
     and the field off the tail's line is THIS rule: since 195 §E.3 the tail
     holds the switch as its SECOND grid line, so on row 2 the tail is 49 px
     tall (30 + 6 + 13) and `align-items: center` centred the title and the
     text box on the whole tail — 9.5 px below the fields' line, where row 1
     (no switch) sat exactly on it. So the row aligns its items on their
     FIRST BASELINE: the title's text, the field's text and the tail's first
     row (a grid's first baseline is its first row's) share one line, and the
     switch stays alone beneath, under « De » (194E §B). MEASURED after, 1600:
     row 2's title and field centres 594.5 → 585 = the tail's first-row
     centre, the title's baseline on « Période »'s, row 1 unmoved; at 1280 the
     tail still drops whole to the second line and at 1024 / 899 the stacked
     form is byte-identical — baseline alignment binds items on ONE flex line
     only. No wrapper, no height, no host-specific number. */
  const rowStyle: CSSProperties = {
    display: "flex",
    flexWrap: "wrap",
    columnGap: 6,
    rowGap: 6,
    alignItems: "first baseline",
    padding: "8px 10px",
    border: "1px solid var(--br)",
    borderRadius: RAD.r8,
    background: "var(--bd)",
  };
  const tailStyle: CSSProperties = {
    display: "grid",
    /* the tracks are App.css `.commTail`'s — a container query decides */
    columnGap: 6,
    rowGap: 6,
    alignItems: "center",
    marginLeft: "auto",
  };
  /* the parties field and the text box: the field that fills the middle,
     cut at the end with an ellipsis when the room runs out */
  const fillingFieldStyle: CSSProperties = {
    flex: "1 1 140px", // 195 §E.3 — the field gives; its 140px floor decides whether the tail keeps the line
    minWidth: 140,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  const setWindow = (kind: EmailSearchKind, w: EmailSearchWindow) => {
    onChange(kind === "quick" ? { ...value, quick: w } : { ...value, deep: w });
  };

  const run = (kind: EmailSearchKind) => {
    const [a, b] = kind === "quick" ? [quickFromRef.current, quickToRef.current] : [deepFromRef.current, deepToRef.current];
    const refusal = dateBoxRefusal(a, b, t);
    if (refusal) {
      onRefuse(refusal);
      return;
    }
    if (kind === "deep" && !value.q.trim()) {
      onRefuse(t("emailSearch.query_empty", { fallback: "Search query is empty" }));
      return;
    }
    // 211 §10 — the parties could not load: the row says so, and the search
    // says it too, never « no party ticked » of a list never read
    if (kind === "quick" && partiesNotLoaded) {
      onRefuse(t("common.list_load_failed", { fallback: "The list could not be loaded" }));
      return;
    }
    // a quick search with no party address ticked (or a matter with none)
    // has nothing to ask — said here, before a request that would answer nothing
    if (kind === "quick" && chosenPartyAddresses(value, parties).length === 0) {
      onRefuse(t("emailSearch.rows.no_party_chosen", { fallback: "No party address is ticked — tick one, or use the free-text search." }));
      return;
    }
    onSearch(kind);
  };

  /** Period · [menu] · [from] [to] · [Search] — the tail every row shares
   *  (PeriodWindowFields, the messages drawer's too), then this row's Search.
   *  197B §A.1 [RULED — Laurent, 2026-09-19, REVERSING 197 B.4(1)]: the tail is
   *  on EVERY host's rows again — the drawer's two rows are the siblings of
   *  the Communication section's, one component, one grammar (194 §C.1: a new
   *  search never needs the drawer closed); the drawer's filter row narrows
   *  the FETCHED rows and never re-searches. The text below is 7e86ada3's,
   *  restored, not rewritten. */
  const windowControls = (kind: EmailSearchKind, w: EmailSearchWindow, fromRef: RefObject<HTMLInputElement>, toRef: RefObject<HTMLInputElement>, below?: ReactNode) => (
    <div className="commTail" style={tailStyle}>
      <PeriodWindowFields
        t={t}
        value={w}
        onChange={(nw) => {
          setWindow(kind, nw);
        }}
        options={periodOptions}
        axisInput={firmSettings}
        {...(menuZIndex != null ? { menuZIndex } : {})}
        fromRef={fromRef}
        toRef={toRef}
      >
        <button
          type="button"
          className="btn-unified btn-unified-primary"
          disabled={busy || disabled}
          onClick={() => {
            run(kind);
          }}
          style={{ height: buttonHeight, boxSizing: "border-box", justifySelf: "end" }}
        >
          {busy ? t("emailSearch.searching", { fallback: "Searching…" }) : t("emailSearch.search", { fallback: "Search" })}
        </button>
      </PeriodWindowFields>
      {below}
    </div>
  );

  const chosenAddresses = parties ? chosenPartyAddresses(value, parties) : [];
  const partyCount = chosenAddresses.length;
  /* 194D B.2 — the field reads the count, then the chosen addresses: a `_one`
     row for one party, a `_zero` row (no list) for none, the base row for the
     rest; cut at the END with an ellipsis by the field's own style. */
  const partiesFieldText = t("emailSearch.rows.parties_field", {
    count: partyCount,
    list: chosenAddresses.join(" ; "),
    fallback: `${String(partyCount)} parties: ${chosenAddresses.join(" ; ")}`,
  });
  const allParties = (parties ?? []).map((p) => p.email);
  const isChosen = (email: string) => value.addresses === null || value.addresses.includes(email);
  const toggleParty = (email: string) => {
    const next = new Set(value.addresses ?? allParties);
    if (next.has(email)) next.delete(email);
    else next.add(email);
    // every party ticked is the default again — `null`, so a party added to
    // the matter later is searched without a visit to this modal
    onChange({ ...value, addresses: next.size === allParties.length && allParties.every((e) => next.has(e)) ? null : [...next] });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {parties !== null && (
        <div className="commRow" style={rowStyle}>
          <span style={titleOf("quick")}>{t("emailSearch.rows.quick_label", { fallback: "Quick search on the parties' addresses:" })}</span>
          <ListNotLoaded lists={PARTY_LISTS} variant="inline">
            <button
              type="button"
              className="field-input commField"
              style={{ ...fillingFieldStyle, textAlign: "left", cursor: "pointer", fontFamily: "inherit", color: partyCount ? "var(--tp)" : "var(--tm)", ...fieldOf("quick") }}
              onClick={() => {
                setPartiesOpen(true);
              }}
              title={t("emailSearch.rows.parties_choose_title", { fallback: "Choose the parties to search" })}
            >
              {partiesFieldText}
            </button>
          </ListNotLoaded>
          {windowControls("quick", value.quick, quickFromRef, quickToRef)}
        </div>
      )}
      <div className="commRow" style={rowStyle}>
        <span style={titleOf("deep")}>{t("emailSearch.rows.deep_label", { fallback: "Free-text search:" })}</span>
        <input
          /* 213B §8 — a field that speaks of addresses is not a sign-in field (src/lib/noAutofill.ts) */
          {...NO_AUTOFILL}
          className="field-input commField no-contact-autofill"
          type="text"
          value={value.q}
          onChange={(e) => {
            onChange({ ...value, q: e.target.value });
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") run("deep");
          }}
          placeholder={t("emailSearch.rows.deep_placeholder", { fallback: "Words or addresses — comma or space = either, AND = both" })}
          title={t("emailSearch.rows.deep_help", { fallback: "Every word must appear somewhere in the message — a quoted phrase is its words, not their order. Comma or space between terms = either; AND (or +) = both." })}
          style={{ ...fillingFieldStyle, fontFamily: "'JetBrains Mono', monospace", ...fieldOf("deep") }}
        />
        {windowControls(
          "deep",
          value.deep,
          deepFromRef,
          deepToRef,
          /* 194E §B [RULED — Laurent, 2026-09-18; supersedes 194D B.5] — the
             switch on its own line, its left edge on the « De » LABEL's: the
             tail's second grid line starts at track 3, the label's (the tail's
             seven tracks: 2 is the menu, 3 is « De »). 195 §E.3 put it inside
             the tail with the tracks it aligns to, so it follows the tail
             wherever the row puts it. */
          <EmailWalkSwitch
            t={t}
            checked={value.walk}
            onChange={(v) => {
              onChange({ ...value, walk: v });
            }}
            className="commSwitch"
            style={{ justifySelf: "start" }}
          />,
        )}
      </div>

      {partiesOpen && parties !== null && (
        <ModalLayout
          title={t("emailSearch.rows.parties_title", { fallback: "The parties' addresses" })}
          onClose={() => {
            setPartiesOpen(false);
          }}
          rawBody
          footer={null}
          maxWidth={520}
          escToClose
          {...(modalZIndex != null ? { zIndex: modalZIndex } : {})}
        >
          <div style={{ padding: "16px 20px" }}>
            {parties.length === 0 ? (
              <p style={{ fontSize: FS.s11, color: "var(--tm)" }}>
                {t("emailSearch.rows.parties_none", { fallback: "This matter has no party with an e-mail address." })}
              </p>
            ) : (
              <>
                <p style={{ fontSize: FS.s10_5, color: "var(--tm)", margin: "0 0 8px" }}>
                  {t("emailSearch.rows.parties_help", { fallback: "Each ticked address is searched; untick the ones to leave out." })}
                </p>
                <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    style={{ padding: "2px 8px", fontSize: FS.s10 }}
                    onClick={() => {
                      onChange({ ...value, addresses: null });
                    }}
                  >
                    {t("emailSearch.rows.parties_all", { fallback: "All" })}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    style={{ padding: "2px 8px", fontSize: FS.s10 }}
                    onClick={() => {
                      onChange({ ...value, addresses: [] });
                    }}
                  >
                    {t("emailSearch.rows.parties_clear", { fallback: "None" })}
                  </button>
                </div>
                <div style={{ marginBottom: 10 }}>
                  {parties.map((p) => (
                    <label
                      key={p.email}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "4px 0",
                        borderBottom: "1px solid var(--br)",
                        fontSize: FS.s11,
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isChosen(p.email)}
                        onChange={() => {
                          toggleParty(p.email);
                        }}
                      />
                      <span style={{ fontWeight: 500, flex: 1 }}>{p.name || p.email}</span>
                      <span style={{ color: "var(--ts)", fontSize: FS.s10 }}>{partyRoleWord(p.role, t)}</span>
                      <span style={{ color: "var(--tm)", fontSize: FS.s10 }}>{p.email}</span>
                    </label>
                  ))}
                </div>
              </>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                type="button"
                className="modal-save"
                onClick={() => {
                  setPartiesOpen(false);
                }}
              >
                {t("common.close", { fallback: "Close" })}
              </button>
            </div>
          </div>
        </ModalLayout>
      )}
    </div>
  );
}
